import { useCallback, useEffect, useState } from 'react';
import { Database, Users, ChefHat, Utensils, Settings } from 'lucide-react';
import {
  DataSyncRun,
  getActiveDataSyncRun,
  getDataSyncRun,
} from '../../../utils/dataSyncApi';
import { OrganizationsSyncTab } from './OrganizationsSyncTab';
import { RecipesSyncTab } from './RecipesSyncTab';
import { IngredientsSyncTab } from './IngredientsSyncTab';
import { ConfigSyncTab } from './ConfigSyncTab';

type SubTab = 'organizations' | 'recipes' | 'ingredients' | 'config';

const TABS: { id: SubTab; label: string; icon: typeof Users }[] = [
  { id: 'organizations', label: 'Organisations', icon: Users },
  { id: 'recipes', label: 'Recettes', icon: ChefHat },
  { id: 'ingredients', label: 'Ingrédients', icon: Utensils },
  { id: 'config', label: 'Config', icon: Settings },
];

export function DataSyncPage() {
  const [subTab, setSubTab] = useState<SubTab>('organizations');
  const [activeRun, setActiveRun] = useState<DataSyncRun | null>(null);
  const [pollingRunId, setPollingRunId] = useState<string | null>(null);

  // Load any active run on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const run = await getActiveDataSyncRun();
        if (cancelled || !run) return;
        setActiveRun(run);
        if (run.status === 'pending' || run.status === 'running') {
          setPollingRunId(run.id);
        }
      } catch {
        // ignore — config may not be ready / auth edge cases
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Poll while a run is active
  useEffect(() => {
    if (!pollingRunId) return;
    let cancelled = false;

    const tick = async () => {
      try {
        const run = await getDataSyncRun(pollingRunId);
        if (cancelled) return;
        setActiveRun(run);
        if (run.status === 'completed' || run.status === 'failed') {
          setPollingRunId(null);
        }
      } catch {
        if (!cancelled) setPollingRunId(null);
      }
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [pollingRunId]);

  const onRunStarted = useCallback((runId: string) => {
    setPollingRunId(runId);
    setActiveRun({
      id: runId,
      type: 'organizations',
      status: 'pending',
      total: 0,
      processed: 0,
      copied: 0,
      overwritten: 0,
      failed: 0,
      errors: [],
      currentLabel: 'Queued…',
    });
  }, []);

  const runDisabled =
    !!pollingRunId || activeRun?.status === 'pending' || activeRun?.status === 'running';

  // Show progress on non-config tabs; keep last completed run visible
  const runForTabs =
    activeRun && (runDisabled || activeRun.status === 'completed' || activeRun.status === 'failed')
      ? activeRun
      : null;

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-3">
        <Database className="h-6 w-6 text-menioo-green" />
        <div>
          <h2 className="text-xl font-display font-bold text-menioo-navy">Data Sync</h2>
          <p className="text-sm text-gray-500">
            Copie prod → préprod (IDs conservés, écrasement idempotent)
          </p>
        </div>
      </div>

      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-6 overflow-x-auto">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => setSubTab(id)}
              className={`inline-flex items-center gap-2 whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium ${
                subTab === id
                  ? 'border-menioo-green text-menioo-green'
                  : 'border-transparent text-gray-500 hover:text-menioo-navy hover:border-gray-300'
              }`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </nav>
      </div>

      {subTab === 'organizations' && (
        <OrganizationsSyncTab
          activeRun={runForTabs}
          onRunStarted={onRunStarted}
          runDisabled={!!runDisabled}
        />
      )}
      {subTab === 'recipes' && (
        <RecipesSyncTab
          activeRun={runForTabs}
          onRunStarted={onRunStarted}
          runDisabled={!!runDisabled}
        />
      )}
      {subTab === 'ingredients' && (
        <IngredientsSyncTab
          activeRun={runForTabs}
          onRunStarted={onRunStarted}
          runDisabled={!!runDisabled}
        />
      )}
      {subTab === 'config' && <ConfigSyncTab />}
    </div>
  );
}
